import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { FilePlus2, Clock, FileCheck2, CreditCard, ChevronRight, TrendingUp, FileWarning } from "lucide-react";
import { AuswahlHub } from "@/pages/admin/AuswahlHub";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const eur = (v) => `${Number(v || 0).toFixed(2).replace(".", ",")} €`;

// Ein Einstieg für alle Rechnungswege — frei, aus bestätigten Stunden oder aus
// einem online angenommenen Angebot (Positionen und Steuersatz kommen mit).
export default function RechnungHub({ pwd, onOpenTab, onCreateInvoice }) {
  const [angenommen, setAngenommen] = useState([]);
  const [zeigeAngebote, setZeigeAngebote] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/angebotsseiten`, {
        headers: { "X-Admin-Password": pwd },
      });
      setAngenommen((data || []).filter((a) => a.status === "angenommen"));
    } catch { /* optional */ }
  }, [pwd]);

  useEffect(() => { load(); }, [load]);

  const ausAngebot = (a) => {
    const s = a.summen || {};
    const privat = (a.kundentyp || "firma") === "privat";
    const menu = (a.gaenge || []).map((g) => g.titel).filter(Boolean).join(" · ");
    onCreateInvoice?.({
      form: {
        betrieb: a.firma || a.ansprechpartner || "",
        ansprechpartner: a.ansprechpartner || "",
        email: a.email || "",
        telefon: a.telefon || "",
        ort: a.ort || "",
        einsatzdatum: a.datum || new Date().toISOString().slice(0, 10),
        leistungszeitraum: "",
        notes: `Gemäß Angebot ${a.nummer}, online angenommen${
          a.angenommen_von ? ` von ${a.angenommen_von}` : ""}.`,
        admin_notes: `Angebotsseite ${a.nummer} · ${a.url}`,
      },
      lineItems: [{
        type_key: "sonstiges",
        name: a.anlass ? `${a.anlass} — Menü pro Person` : "Menü pro Person",
        description: menu,
        quantity: s.personen || 1,
        unit_name: "Person",
        unit_price_net: s.pro_person_netto || 0,
        tax_rate: privat ? 7 : 19,
      }],
    });
    toast.success(`Rechnung aus Angebot ${a.nummer} vorbereitet — ${privat ? "7" : "19"} % MwSt`);
  };

  const karten = [
    {
      id: "frei", icon: FilePlus2, label: "Freie Rechnung",
      hint: "Positionen selbst eintragen — für spontane Einsätze ohne Angebot.",
      onClick: () => onOpenTab?.("direct-invoices", { openCreate: true }),
    },
    {
      id: "aus-stunden", icon: Clock, label: "Aus bestätigten Stunden",
      hint: "Offene Stundennachweise eines Kunden zu einer Sammelrechnung bündeln.",
      onClick: () => onOpenTab?.("stundenkonto"),
    },
    {
      id: "aus-angebot", icon: FileCheck2, label: "Aus angenommenem Angebot",
      hint: "Menü, Personen und der richtige Steuersatz kommen automatisch mit.",
      badge: angenommen.length || null,
      onClick: () => setZeigeAngebote((v) => !v),
    },
  ];

  return (
    <AuswahlHub
      testid="rechnung-hub"
      titel="Rechnung schreiben"
      hinweis="Wähle, woher die Positionen kommen. Alle Wege landen in derselben Rechnungsmaske — Lexoffice und Versand bleiben unverändert."
      karten={karten}
      extra={
        <div className="space-y-6">
          {zeigeAngebote && (
            <div className="border border-[#E3DECE] bg-white p-4 animate-in fade-in slide-in-from-top-1 duration-200"
              data-testid="rechnung-aus-angebot-liste">
              <p className="text-[10px] uppercase tracking-[0.2em] text-[#BA6D52] mb-3">
                Angenommene Angebote
              </p>
              {angenommen.length === 0 ? (
                <p className="text-sm text-[#757067] italic">
                  Noch kein Angebot online angenommen — sobald ein Kunde zusagt, erscheint es hier.
                </p>
              ) : (
                <div className="space-y-2">
                  {angenommen.map((a) => (
                    <button key={a.id} onClick={() => ausAngebot(a)}
                      data-testid={`rechnung-aus-angebot-${a.id}`}
                      className="w-full text-left border border-[#E3DECE] hover:border-[#BA6D52] bg-[#FAFAF7] px-4 py-3 flex items-center justify-between gap-4 transition-colors">
                      <span>
                        <span className="block font-serif text-base text-[#2C2A26]">
                          {a.firma || a.ansprechpartner}
                        </span>
                        <span className="block text-xs text-[#757067]">
                          {[a.nummer, a.anlass, a.datum ? a.datum.split("-").reverse().join(".") : null,
                            `${a.summen?.personen || 0} Personen`,
                            `${(a.kundentyp || "firma") === "privat" ? "7" : "19"} % MwSt`]
                            .filter(Boolean).join(" · ")}
                        </span>
                      </span>
                      <span className="text-right shrink-0">
                        <span className="block text-sm text-[#BA6D52]">{eur(a.summen?.netto)}</span>
                        <span className="block text-[10px] uppercase tracking-[0.15em] text-[#757067]">netto</span>
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          <button onClick={() => onOpenTab?.("auswertung")} data-testid="rechnung-hub-auswertung"
            className="w-full border border-[#E3DECE] bg-white hover:border-[#BA6D52] px-5 py-4 flex items-center justify-between gap-4 transition-colors">
            <span className="inline-flex items-center gap-3">
              <TrendingUp size={18} className="text-[#BA6D52]" />
              <span className="text-left">
                <span className="block font-serif text-lg text-[#2C2A26]">Auswertung</span>
                <span className="block text-xs text-[#757067]">
                  Was eingenommen wurde, was noch offen ist und was nächsten Monat reinkommt
                </span>
              </span>
            </span>
            <ChevronRight size={16} className="text-[#BA6D52]" />
          </button>

          <button onClick={() => onOpenTab?.("mahnungen")} data-testid="rechnung-hub-mahnungen"
            className="w-full border border-[#E3DECE] bg-white hover:border-[#BA6D52] px-5 py-4 flex items-center justify-between gap-4 transition-colors">
            <span className="inline-flex items-center gap-3">
              <FileWarning size={18} className="text-[#BA6D52]" />
              <span className="text-left">
                <span className="block font-serif text-lg text-[#2C2A26]">Mahnungen</span>
                <span className="block text-xs text-[#757067]">
                  Überfällige Rechnungen anmahnen — Erinnerung, Mahnung, letzte Mahnung
                </span>
              </span>
            </span>
            <ChevronRight size={16} className="text-[#BA6D52]" />
          </button>

          <button onClick={() => onOpenTab?.("payments")} data-testid="rechnung-hub-zahlungen"
            className="w-full border border-[#E3DECE] bg-white hover:border-[#BA6D52] px-5 py-4 flex items-center justify-between gap-4 transition-colors">
            <span className="inline-flex items-center gap-3">
              <CreditCard size={18} className="text-[#BA6D52]" />
              <span className="text-left">
                <span className="block font-serif text-lg text-[#2C2A26]">Rechnungen &amp; Zahlungen</span>
                <span className="block text-xs text-[#757067]">Alle gestellten Rechnungen, offen und bezahlt</span>
              </span>
            </span>
            <ChevronRight size={16} className="text-[#BA6D52]" />
          </button>
        </div>
      }
    />
  );
}
